import { Box, Text, Tabs, TabsTab, TabsList, Table, TableThead, TableTbody, Badge, Flex } from "@mantine/core"
import { BoxPlayer, Boxscore, VideoEvent } from "../../client"
import { flexRender, getCoreRowModel, useReactTable, Table as TanStackTable, HeaderGroup, Header, Row, Cell } from '@tanstack/react-table'
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { BoxscoreTable, OverviewTable } from "../../models/Tables"
import { boxscoreColumns as bx, overviewColumns as ov } from "../../models/Tables"

interface VideoStatsProps {
    video: VideoEvent
    boxscore: Boxscore | null
}

const VideoStats: React.FC<VideoStatsProps> = ({ video, boxscore }) => {
    const [homeBoxscoreData, setHomeBoxscoreData] = useState<BoxscoreTable[]>([])
    const [awayBoxscoreData, setAwayBoxscoreData] = useState<BoxscoreTable[]>([])
    const [overviewData, setOverviewData] = useState<OverviewTable[]>([])

    // Parse search parameters to detect searched player or stats
    const [searchParams] = useSearchParams();
    const fromQuery = searchParams.get('from');
    const searchData = useMemo(() => {
      if (!fromQuery) return null;
      try {
        return Object.fromEntries(new URLSearchParams(decodeURIComponent(fromQuery)));
      } catch {
        return null;
      }
    }, [fromQuery]);

    const searchedPlayerId = searchData?.type === "player" ? searchData.player : undefined;
    const activePlayerId = searchedPlayerId || video.playerId;
    const activePlayerName = (searchedPlayerId && searchedPlayerId === video.player2Id ? video.player2Name : video.playerName) || undefined;

    // Determine active player's team tab deterministically
    const activePlayerTeam = useMemo<"home" | "away" | null>(() => {
      if (!boxscore || !activePlayerId) return null;

      // 1. Direct roster check by player personId
      if (boxscore.homeTeam.players.some((p) => String(p.personId) === String(activePlayerId))) {
        return "home";
      }
      if (boxscore.awayTeam.players.some((p) => String(p.personId) === String(activePlayerId))) {
        return "away";
      }

      // 2. Team ID fallback from video event
      const playerTeamId = String(activePlayerId) === String(video.player2Id)
        ? video.player2TeamId
        : video.playerTeamId;

      if (playerTeamId) {
        if (String(playerTeamId) === String(boxscore.homeTeam.teamId)) return "home";
        if (String(playerTeamId) === String(boxscore.awayTeam.teamId)) return "away";
      }

      return null;
    }, [boxscore, activePlayerId, video]);

    // Active tab state: defaults and auto-switches to the active player's team tab
    const [activeTab, setActiveTab] = useState<string | null>("home");

    useEffect(() => {
      if (activePlayerTeam) {
        setActiveTab(activePlayerTeam);
      } else if (boxscore) {
        setActiveTab("home");
      }
    }, [video?.gameId, video?.eventNumber, activePlayerTeam, boxscore]);

    const mapPlayerData = (player: BoxPlayer): BoxscoreTable => {
      const stats = player.statistics;
      const fullName = `${player.firstName || ""} ${player.familyName || ""}`.trim();
      const displayName = player.nameI || player.familyName || fullName || player.name;
      if (!stats) {
        return {
          personId: player.personId,
          firstName: player.firstName,
          familyName: player.familyName,
          fullName: fullName || player.name,
          name: displayName,
          min: "0",
          ast: 0,
          blk: 0,
          pts: 0,
          reb: 0,
          stl: 0,
          tov: 0,
          plusMinus: 0,
          pf: 0,
          fg: "0-0",
          fg3: "0-0",
          ft: "0-0",
        };
      }
      return {
        personId: player.personId,
        firstName: player.firstName,
        familyName: player.familyName,
        fullName: fullName || player.name,
        name: displayName,
        min: stats.minutes ? stats.minutes.replace('PT', '').split("M")[0] : "0",
        ast: stats.assists ?? 0,
        blk: stats.blocks ?? 0,
        pts: stats.points ?? 0,
        reb: stats.reboundsTotal ?? stats.reboundsDefensive ?? 0,
        stl: stats.steals ?? 0,
        tov: stats.turnovers ?? 0,
        plusMinus: stats.plusMinusPoints ?? 0,
        pf: stats.foulsPersonal ?? 0,
        fg: `${stats.fieldGoalsMade}-${stats.fieldGoalsAttempted}`,
        fg3: `${stats.threePointersMade}-${stats.threePointersAttempted}`,
        ft: `${stats.freeThrowsMade}-${stats.freeThrowsAttempted}`,
      };
    };

    useEffect(() => {
        if (!boxscore) return
        setOverviewData([
        {
            stat: "Score",
            home: boxscore.homeTeam.score.toString(),
            away: boxscore.awayTeam.score.toString(),
        },
        {
            stat: "FG",
            home: `${boxscore.homeTeam.statistics.fieldGoalsMade}-${boxscore.homeTeam.statistics.fieldGoalsAttempted} (${Math.round(boxscore.homeTeam.statistics.fieldGoalsPercentage * 10000)/100}%)`,
            away: `${boxscore.awayTeam.statistics.fieldGoalsMade}-${boxscore.awayTeam.statistics.fieldGoalsAttempted} (${Math.round(boxscore.homeTeam.statistics.fieldGoalsPercentage * 10000)/100}%)`,
        },
        {
            stat: "3PM",
            home: `${boxscore.homeTeam.statistics.threePointersMade}-${boxscore.homeTeam.statistics.threePointersAttempted} (${Math.round(boxscore.homeTeam.statistics.threePointersPercentage * 10000)/100}%)`,
            away: `${boxscore.awayTeam.statistics.threePointersMade}-${boxscore.awayTeam.statistics.threePointersAttempted} (${Math.round(boxscore.awayTeam.statistics.threePointersPercentage * 10000)/100}%)`,
        },
        {
            stat: "FT",
            home: `${boxscore.homeTeam.statistics.freeThrowsMade}-${boxscore.homeTeam.statistics.freeThrowsAttempted} (${Math.round(boxscore.homeTeam.statistics.freeThrowsPercentage * 10000)/100}%)`,
            away: `${boxscore.awayTeam.statistics.freeThrowsMade}-${boxscore.awayTeam.statistics.freeThrowsAttempted} (${Math.round(boxscore.awayTeam.statistics.freeThrowsPercentage * 10000)/100}%)`,
        },
        {
            stat: "Rebounds",
            home: `${boxscore.homeTeam.statistics.reboundsTotal}`,
            away: `${boxscore.awayTeam.statistics.reboundsTotal}`,
        },
        {
            stat: "Offensive Rebounds",
            home: `${boxscore.homeTeam.statistics.reboundsOffensive + boxscore.homeTeam.statistics.reboundsTeamOffensive}`,
            away: `${boxscore.awayTeam.statistics.reboundsOffensive + boxscore.awayTeam.statistics.reboundsTeamOffensive}`,
        },
        {
            stat: "Defensive Rebounds",
            home: `${boxscore.homeTeam.statistics.reboundsDefensive + boxscore.homeTeam.statistics.reboundsTeamDefensive }`,
            away: `${boxscore.awayTeam.statistics.reboundsDefensive + boxscore.awayTeam.statistics.reboundsTeamDefensive}`,
        },
        {
            stat: "Assists",
            home: `${boxscore.homeTeam.statistics.assists}`,
            away: `${boxscore.awayTeam.statistics.assists}`,
        },
        {
            stat: "Steals",
            home: `${boxscore.homeTeam.statistics.steals}`,
            away: `${boxscore.awayTeam.statistics.steals}`,
        },
        {
            stat: "Blocks",
            home: `${boxscore.homeTeam.statistics.blocks}`,
            away: `${boxscore.awayTeam.statistics.blocks}`,
        },
        {
            stat: "Turnovers",
            home: `${boxscore.homeTeam.statistics.turnoversTotal}`,
            away: `${boxscore.awayTeam.statistics.turnoversTotal}`,
        },
        {
            stat: "Points Off Turnovers",
            home: `${boxscore.homeTeam.statistics.pointsFromTurnovers}`,
            away: `${boxscore.awayTeam.statistics.pointsFromTurnovers}`,
        },
        {
            stat: "Fastbreak Points",
            home: `${boxscore.homeTeam.statistics.pointsFastBreak}`,
            away: `${boxscore.awayTeam.statistics.pointsFastBreak}`,
        },
        {
            stat: "Points in Paint",
            home: `${boxscore.homeTeam.statistics.pointsInThePaint}`,
            away: `${boxscore.awayTeam.statistics.pointsInThePaint}`,
        },
        {
            stat: "Second Chance Points",
            home: `${boxscore.homeTeam.statistics.pointsSecondChance}`,
            away: `${boxscore.awayTeam.statistics.pointsSecondChance}`,
        },
        {
            stat: "Fouls",
            home: `${boxscore.homeTeam.statistics.foulsTeam}`,
            away: `${boxscore.awayTeam.statistics.foulsTeam}`,
        },
        {
            stat: "Technical Fouls",
            home: `${boxscore.homeTeam.statistics.foulsTechnical}`,
            away: `${boxscore.awayTeam.statistics.foulsTechnical}`,
        },
        {
            stat: "Biggest Lead",
            home: `${boxscore.homeTeam.statistics.biggestLead}`,
            away: `${boxscore.awayTeam.statistics.biggestLead}`,
        }])
        setHomeBoxscoreData(boxscore.homeTeam.players.map(mapPlayerData))
        setAwayBoxscoreData(boxscore.awayTeam.players.map(mapPlayerData))
    }, [boxscore])

    const overviewColumns = useMemo(() => ov, [])
    const overviewTable = useReactTable({
        columns: overviewColumns,
        data: overviewData,
        getCoreRowModel: getCoreRowModel()
    })

    const boxscoreColumns = useMemo(() => bx, [])
    const homeBoxscoreTable = useReactTable({
        columns: boxscoreColumns,
        data: homeBoxscoreData,
        getCoreRowModel: getCoreRowModel()
    })
    const awayBoxscoreTable = useReactTable({
        columns: boxscoreColumns,
        data: awayBoxscoreData,
        getCoreRowModel: getCoreRowModel()
    })

    const renderBoxscoreTable = (table: TanStackTable<BoxscoreTable>, teamName?: string) => {
      return (
        <div className="flex flex-col gap-4 items-center w-full">
          <Text pt={10} ta="center" size="lg" fw={800} className="text-center">
            {teamName} Stats
          </Text>
          <Box
            pt={10}
            style={{
              width: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table style={{ minWidth: 640 }}>
              <TableThead>
                {table.getHeaderGroups().map((headerGroup: HeaderGroup<BoxscoreTable>) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header: Header<BoxscoreTable, unknown>) => (
                      <th
                        key={header.id}
                        style={{
                          textAlign: header.column.id === "name" ? "left" : "center",
                          padding: "8px 10px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </TableThead>
              <TableTbody ta="center">
                {table.getRowModel().rows.map((row: Row<BoxscoreTable>) => {
                  const isPlayerActive = row.original.personId && activePlayerId
                    ? String(row.original.personId) === String(activePlayerId)
                    : activePlayerName
                    ? row.original.name?.toLowerCase() === activePlayerName.toLowerCase()
                    : false;

                  return (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor: isPlayerActive ? "var(--mantine-color-accent-light)" : "transparent",
                        transition: "background-color 150ms ease",
                      }}
                    >
                      {row.getVisibleCells().map((cell: Cell<BoxscoreTable, unknown>, cellIdx: number) => {
                        const isNameCol = cell.column.id === "name";

                        const borderLeftStyle = cellIdx === 0
                          ? isPlayerActive
                            ? "3px solid var(--mantine-color-accent-filled)"
                            : "3px solid transparent"
                          : undefined;

                        return (
                          <td
                            key={cell.id}
                            style={{
                              fontWeight: isNameCol ? 600 : 400,
                              borderLeft: borderLeftStyle,
                              textAlign: isNameCol ? "left" : "center",
                              padding: "8px 10px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isNameCol ? (
                              <Flex align="center" gap={6}>
                                <span
                                  style={{
                                    color: isPlayerActive ? "var(--mantine-color-accent-light-color)" : undefined,
                                    fontWeight: isPlayerActive ? 700 : 500,
                                  }}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </span>
                                {isPlayerActive && (
                                  <Badge size="xs" variant="filled" color="accent" radius="sm">
                                    Active
                                  </Badge>
                                )}
                              </Flex>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </TableTbody>
            </Table>
          </Box>
        </div>
      );
    };

    return (
        <div className="">
            <Tabs value={activeTab} onChange={setActiveTab} variant="default">
                <TabsList styles={{ list: { width: "100%"}}}>
                <TabsTab value="home" styles={{ tab: { flex: 1 } }}>
                    <Flex align="center" justify="center" gap={6}>
                        <Text fw={600} size="sm">{ boxscore?.homeTeam.teamName || "Home" }</Text>
                        {activePlayerTeam === "home" && (
                            <Badge size="xs" variant="filled" color="accent" radius="sm">
                                Active
                            </Badge>
                        )}
                    </Flex>
                </TabsTab>
                <TabsTab value="overview" styles={{ tab: { flex: 1 } }}>
                    <Text fw={600} size="sm">Overview</Text>
                </TabsTab>
                <TabsTab value="away" styles={{ tab: { flex: 1 } }}>
                    <Flex align="center" justify="center" gap={6}>
                        <Text fw={600} size="sm">{ boxscore?.awayTeam.teamName || "Away" }</Text>
                        {activePlayerTeam === "away" && (
                            <Badge size="xs" variant="filled" color="accent" radius="sm">
                                Active
                            </Badge>
                        )}
                    </Flex>
                </TabsTab>
                </TabsList>

                <Tabs.Panel value="overview" className="pt-6">
                <div className="flex flex-col gap-4 items-center w-full">
                    <Box pt={10} style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                    <Table style={{ minWidth: 400 }}>
                        <TableTbody ta="center">
                            {overviewTable.getRowModel().rows.map((row) => {
                                return (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td key={cell.id} style={{fontWeight: cell.getContext().column.id === "stat" ? "800" : "400", padding: "8px 12px"}}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                            );
                                        })}
                                    </tr>
                                )
                            })}
                        </TableTbody>
                    </Table>
                    </Box>
                </div>
                </Tabs.Panel>

                <Tabs.Panel value="home" className="pt-6">
                    {renderBoxscoreTable(homeBoxscoreTable, boxscore?.homeTeam.teamName)}
                </Tabs.Panel>

                <Tabs.Panel value="away" className="pt-6">
                    {renderBoxscoreTable(awayBoxscoreTable, boxscore?.awayTeam.teamName)}
                </Tabs.Panel>
            </Tabs>
        </div>
    )
}

export default VideoStats